/**
 * Client helper: call the Render `server/` PDF microservice and save the PDF blob.
 * Requires `REACT_APP_PDF_SERVICE_URL` and a valid `Authorization` header (Supabase JWT recommended).
 */

/**
 * Origin for the print page URL that Puppeteer opens server-side.
 *
 * On Render, `http://localhost:3000` is the container itself, not your CRA dev server — you get
 * `ERR_CONNECTION_REFUSED`. When testing local UI against a remote PDF service, set
 * `REACT_APP_PDF_PRINT_ORIGIN` to your publicly reachable app URL (e.g. production).
 */
export function resolvePrintPageOrigin(): string {
  // Avoid `?.trim()` so CRA DefinePlugin inlining stays unambiguous after Babel.
  const fromEnv = (process.env.REACT_APP_PDF_PRINT_ORIGIN || "").trim();
  if (fromEnv.length > 0) {
    return fromEnv.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location.origin.length > 0) {
    return window.location.origin;
  }
  return "";
}

/** Chart blueprint modes the Destiny print route can render. */
export type PrintResultBlueprintMode =
  | "dna"
  | "dayun"
  | "liunian"
  | "liunianNext"
  | "liumonth";

/**
 * Optional timing context for `/print/result` so the PDF matches the tab the
 * user is viewing (DNA / Da Yun / Liu Nian this|next year / Liu Month).
 */
export interface PrintResultExportOptions {
  readonly mode?: PrintResultBlueprintMode;
  /** Lunar month 1–12 when mode is `liumonth`. */
  readonly lunarMonth?: number;
  /**
   * Gregorian year for Liu Month annual flow, or for Liu Nian / Liu Nian next
   * when the print page should pin a specific year.
   */
  readonly solarYear?: number;
}

/**
 * Build the print URL the PDF service will open (allowlisted in `ALLOWED_PDF_ORIGIN`).
 */
export function buildPrintResultTargetUrl(
  appOrigin: string,
  profileId: string,
  pdfToken: string,
  options?: PrintResultExportOptions
): string {
  const trimmedOrigin = appOrigin.replace(/\/$/, "");
  const path = `/print/result/${encodeURIComponent(profileId)}`;
  const url = new URL(path, `${trimmedOrigin}/`);
  url.searchParams.set("pdfToken", pdfToken);
  const mode = options?.mode;
  if (mode !== undefined && mode !== "dna") {
    url.searchParams.set("mode", mode);
  }
  if (mode === "liumonth") {
    const lunarMonth = options?.lunarMonth;
    if (
      typeof lunarMonth === "number" &&
      Number.isInteger(lunarMonth) &&
      lunarMonth >= 1 &&
      lunarMonth <= 12
    ) {
      url.searchParams.set("lunarMonth", String(lunarMonth));
    }
  }
  if (
    mode === "liumonth" ||
    mode === "liunian" ||
    mode === "liunianNext"
  ) {
    const solarYear = options?.solarYear;
    if (typeof solarYear === "number" && Number.isFinite(solarYear)) {
      url.searchParams.set("solarYear", String(Math.trunc(solarYear)));
    }
  }
  return url.toString();
}

/**
 * Download filename that reflects the active blueprint tab.
 */
export function buildPrintResultFilename(
  profileName: string,
  mode: PrintResultBlueprintMode
): string {
  const safeName = profileName.trim().length > 0 ? profileName.trim() : "report";
  const modeLabel =
    mode === "dayun"
      ? "DaYun"
      : mode === "liunian"
        ? "LiuNian"
        : mode === "liunianNext"
          ? "LiuNianNext"
          : mode === "liumonth"
            ? "LiuMonth"
            : "Natal";
  return `Destiny-Blueprint-${modeLabel}-${safeName}.pdf`;
}

/**
 * POST only enqueues a job (or wakes a sleeping free-tier service).
 * Cold start on Render free can take well over a minute before the first byte.
 */
const SERVER_REQUEST_TIMEOUT_MS = 3 * 60_000;

/** One poll round-trip. The PDF itself is not rebuilt on GET. */
const POLL_REQUEST_TIMEOUT_MS = 60_000;

/** How often to ask whether a background export is ready. */
const POLL_INTERVAL_MS = 400;

/**
 * Whole wait for a background job, including time spent in line behind other students.
 * Short polls mean Render never holds one open request for the full Chrome render.
 */
const ASYNC_POLL_DEADLINE_MS = 20 * 60_000;

/**
 * Read `{ error: string }` from a JSON body. Returns null when the shape does not match.
 */
function readErrorMessage(value: unknown): string | null {
  if (typeof value !== "object" || value === null || !("error" in value)) {
    return null;
  }
  const message = value.error;
  return typeof message === "string" && message.length > 0 ? message : null;
}

/**
 * Read `{ jobId: string }` from the 202 body.
 */
function readJobId(value: unknown): string | null {
  if (typeof value !== "object" || value === null || !("jobId" in value)) {
    return null;
  }
  const jobId = value.jobId;
  return typeof jobId === "string" && jobId.length > 0 ? jobId : null;
}

/**
 * Read queue phase from a poll body.
 */
function readQueuePhase(value: unknown): "queued" | "running" | null {
  if (typeof value !== "object" || value === null || !("status" in value)) {
    return null;
  }
  const status = value.status;
  if (status === "queued" || status === "running") {
    return status;
  }
  return null;
}

/**
 * Turn a non-OK response into a sentence the export modal can show.
 */
async function readFailureDetail(response: Response): Promise<string> {
  try {
    const asJson: unknown = await response.json();
    const message = readErrorMessage(asJson);
    if (message !== null) {
      return message;
    }
  } catch {
    try {
      const text = await response.text();
      if (text.length > 0) {
        return text;
      }
    } catch {
      /* fall through to status text */
    }
  }
  return response.statusText;
}

/**
 * fetch with a hard abort so one stuck poll cannot sit until the overall deadline.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Wait `ms` milliseconds.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Save a PDF blob with the browser download API.
 */
function savePdfBlob(blob: Blob, filename: string): void {
  if (blob.size === 0) {
    throw new Error("We could not generate your chart download: empty file.");
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = "noopener";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Poll until the background job returns PDF bytes or a terminal error.
 */
async function pollPdfJob(
  jobUrl: string,
  authorization: string,
  deadlineAt: number
): Promise<Blob> {
  let lastPhase: "queued" | "running" | null = null;

  for (;;) {
    if (Date.now() > deadlineAt) {
      if (lastPhase === "queued") {
        throw new Error(
          "Several reports are generating right now, so yours stayed in line too long. Please try again in a few minutes."
        );
      }
      throw new Error("PDF export timed out. Please try again in a moment.");
    }

    let response: Response;
    try {
      response = await fetchWithTimeout(
        jobUrl,
        { method: "GET", headers: { Authorization: authorization } },
        POLL_REQUEST_TIMEOUT_MS
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        await delay(POLL_INTERVAL_MS);
        continue;
      }
      throw error;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (response.ok && contentType.includes("application/pdf")) {
      return await response.blob();
    }

    if (response.status === 202 || (response.ok && contentType.includes("application/json"))) {
      const payload: unknown = await response.json();
      lastPhase = readQueuePhase(payload);
      await delay(POLL_INTERVAL_MS);
      continue;
    }

    const detail = await readFailureDetail(response);
    throw new Error(`We could not generate your chart download (${String(response.status)}): ${detail}`);
  }
}

/**
 * POST `{ targetUrl, async: true }` to the pdf-server and trigger a file download.
 * The server answers with a job id, then this function polls until the PDF is ready.
 * An older server that ignores `async` and returns the PDF directly still works.
 *
 * @param targetUrl - Full HTTPS (or dev http) URL the server will pass to Puppeteer.
 * @param getAuthHeader - Returns `Authorization` value (e.g. `Bearer <jwt>`) or null if unavailable.
 * @param filename - Download filename in the browser.
 */
export async function exportPdfViaServer(
  targetUrl: string,
  getAuthHeader: () => Promise<string | null>,
  filename = "report.pdf"
): Promise<void> {
  const baseRaw = (process.env.REACT_APP_PDF_SERVICE_URL || "").trim();
  if (baseRaw.length === 0) {
    throw new Error("REACT_APP_PDF_SERVICE_URL is not configured.");
  }

  const headerValue = await getAuthHeader();
  if (headerValue === null || headerValue === "") {
    throw new Error("Please sign in first so we can prepare your chart.");
  }

  const base = baseRaw.replace(/\/$/, "");
  const endpoint = `${base}/api/export-pdf`;

  const authorization = headerValue.startsWith("Bearer ")
    ? headerValue
    : `Bearer ${headerValue}`;

  let response: Response;
  try {
    response = await fetchWithTimeout(
      endpoint,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authorization,
        },
        body: JSON.stringify({ targetUrl, async: true }),
      },
      SERVER_REQUEST_TIMEOUT_MS
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("PDF export timed out. Please try again in a moment.");
    }
    if (error instanceof TypeError) {
      // Browsers surface both "server down" and CORS blocks as TypeError: Failed to fetch.
      throw new Error(
        `Could not reach the PDF service at ${base}. Check that server/ is running on :8787, open the app at http://localhost:3000 (not a LAN IP unless CORS allows it), and hard-refresh so REACT_APP_PDF_SERVICE_URL is current.`
      );
    }
    throw error;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (response.ok && contentType.includes("application/pdf")) {
    savePdfBlob(await response.blob(), filename);
    return;
  }

  if (response.ok && contentType.includes("application/json")) {
    const payload: unknown = await response.json();
    const jobId = readJobId(payload);
    if (jobId === null) {
      throw new Error("We could not generate your chart download: missing export id.");
    }
    const jobUrl = `${endpoint}/jobs/${encodeURIComponent(jobId)}`;
    const blob = await pollPdfJob(jobUrl, authorization, Date.now() + ASYNC_POLL_DEADLINE_MS);
    savePdfBlob(blob, filename);
    return;
  }

  const detail = await readFailureDetail(response);
  throw new Error(`We could not generate your chart download (${String(response.status)}): ${detail}`);
}
